import React, { useLayoutEffect, useRef } from "react";
import useStyle from "./useStyle";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  IconButton,
  Switch,
  FormControlLabel,
  Chip,
  Typography,
} from "@mui/material";
import { Edit as Pencil, Check, Close as X } from "@mui/icons-material";
import { InlineEditTableProps, InlineEditTableColumn } from "./types";
import { v4 } from "uuid";

const InlineEditTable = ({
  items,
  columns,
  editValue,
  isEditing,
  onEdit,
  onCancel,
  onEditValueChange,
  onOpenCategoryModal,
  onSave,
  onToggle,
}: InlineEditTableProps) => {
  const classes = useStyle();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const cursorRef = useRef<{ start: number; end: number } | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    cursorRef.current = {
      start: e.target.selectionStart ?? 0,
      end: e.target.selectionEnd ?? 0,
    };

    onEditValueChange(e.target.value);
  };

  useLayoutEffect(() => {
    const cursor = cursorRef.current;
    if (inputRef.current && cursor) {
      inputRef.current.setSelectionRange(cursor.start, cursor.end);
    }
  }, [editValue]);

  const getTableCell = (column: InlineEditTableColumn, item: any) => {
    if (column.editable) {
      return getEditableCell(column, item);
    } else {
      return getNonEditableCell(column, item);
    }
  };

  const getEditableCell = (column: InlineEditTableColumn, item: any) => {
    switch (column.columnType) {
      default:
      case "text":
        return (
          <TableCell key={v4()}>
            {isEditing(item.id, column.id) ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <TextField
                  inputRef={inputRef}
                  value={editValue}
                  onChange={handleChange}
                  size="small"
                  error={column.getErrors && !!column.getErrors(editValue)}
                  autoFocus
                  fullWidth
                />
                <IconButton
                  size="small"
                  onClick={onSave}
                  sx={{ color: "success.main" }}
                  disabled={column.getErrors && !!column.getErrors(editValue)}
                >
                  <Check fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={onCancel}
                  sx={{ color: "error.main" }}
                >
                  <X fontSize="small" />
                </IconButton>
              </Box>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  "&:hover .edit-button": { opacity: 1 },
                }}
              >
                <span>{item[column.id]}</span>
                <IconButton
                  size="small"
                  className="edit-button"
                  sx={{ opacity: 0, transition: "opacity 0.2s" }}
                  onClick={() => onEdit(item.id, column.id, item[column.id])}
                >
                  <Pencil fontSize="small" />
                </IconButton>
              </Box>
            )}
          </TableCell>
        );
      case "boolean":
        return (
          <TableCell key={v4()}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={item[column.id]}
                    onChange={() => onToggle(item.id, item[column.id])}
                    size="small"
                    color="error"
                  />
                }
                label={
                  <Typography variant="body2" color="success">
                    {item[column.id] ? "Activo" : "Inactivo"}
                  </Typography>
                }
              />
            </Box>
          </TableCell>
        );
      case "select":
        return (
          <TableCell key={v4()}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                "&:hover .edit-button": { opacity: 1 },
              }}
            >
              <Chip label={item[column.id]} size="small" variant="outlined" />
              <IconButton
                size="small"
                className="edit-button"
                sx={{ opacity: 0, transition: "opacity 0.2s" }}
                onClick={() => onOpenCategoryModal(item.id, item[column.id])}
              >
                <Pencil fontSize="small" />
              </IconButton>
            </Box>
          </TableCell>
        );
    }
  };

  const getNonEditableCell = (column: InlineEditTableColumn, item: any) => {
    switch (column.columnType) {
      case "boolean":
        return (
          <TableCell key={v4()}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={item[column.id]}
                    size="small"
                    disabled
                    color="error"
                  />
                }
                label={
                  <Typography variant="body2" color="success">
                    {item[column.id] ? "Activo" : "Inactivo"}
                  </Typography>
                }
              />
            </Box>
          </TableCell>
        );
      case "select":
        return (
          <TableCell key={v4()}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Chip label={item[column.id]} size="small" variant="outlined" />
            </Box>
          </TableCell>
        );
      case "text":
      default:
        return <TableCell>{item[column.id]}</TableCell>;
    }
  };

  return (
    <>
      <TableContainer className={classes.container}>
        <Table
          className={classes.table}
          size="small"
          aria-label="inline editable table"
          atia-labelledby="tableTitle"
        >
          <TableHead className={classes.tableHead}>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={v4()}
                  className={classes.tableCellHead}
                  align={column.headerAlign || "left"}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {items.length === 0 ? (
              <TableRow key={`${v4()}-no-data`}>
                <TableCell colSpan={columns.length} align="center">
                  No hay datos disponibles
                </TableCell>
              </TableRow>
            ) : (
              items.map((item) => (
                <TableRow key={`${v4()}-row-${item.id}`}>
                  {columns.map((column) => getTableCell(column, item))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default InlineEditTable;
